// İSG Portal — Button primitifi (Radix Slot ile `asChild`). Token'lı stil;
// literal renk yok (bkz. tokens.css).

import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from './cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-tertiary';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Alt elemanı buton olarak render eder (örn. `<a>` link). */
  asChild?: boolean;
  /** Yükleniyor — butonu devre dışı bırakır + imleç bekleme. */
  loading?: boolean;
}

// ⚠️ Burada `focus-visible:outline-none` YOKTUR ve EKLENMEMELİDİR. `tokens.css`'in temel
// katmanındaki `*:focus-visible { outline: 2px solid var(--color-ring) }` kuralı TÜM
// portalların klavye odak sinyalidir; bir utility onu ezdiğinde (utility katmanı base'i
// yener) hiçbir portalda hiçbir butonun görünür odağı KALMAZ. Bu bir stil tercihi değil,
// erişilebilirlik gereğidir (WCAG 2.4.7).
const base =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors ' +
  'disabled:pointer-events-none disabled:opacity-60 select-none';

// `carbon:` — S33 Carbon planı §2.4. Yalnız `theme-carbon.css`'i import eden
// build'de (bu dilimde yalnız Storybook, bkz. .storybook/preview.css) var olan
// bir varyant; `primary`/`danger` renk-token değişimiyle KENDİLİĞİNDEN doğru
// olduğu için ek class ALMAZ (0 dosya değişir).
//
// **`secondary` HER YERDE Carbon'un gerçek dolu-gri "Secondary"si (owner review B1 item 4,
// 2026-09-28) — eski `[data-modal-footer]` DOM-kapsamlı ayrım KALDIRILDI.** Önceki sürüm sayfa
// düzeyinde mavi-çerçeveli bir "tertiary" render ediyordu, yalnız `Modal`'ın footer'ı İÇİNDE
// dolu-gri olurdu — review'ün bulgusu tam bu ikiliydi ("Vazgeç" modallerde gri, editör
// sayfalarında (Training/Risk vb.) mavi çerçeveli). Carbon'un GERÇEK "Secondary"si zaten TEK
// biçim (dolu gri) — iki biçim İCAT eden buradaki eski kod, gerçek Carbon'dan sapmaydı.
// `Modal.tsx`'in `data-modal-footer` işareti KALDI (footer'ın kendi grid düzeni İÇİN, dosya
// başlığı notu) ama artık Button bunu OKUMUYOR.
const variants: Record<Variant, string> = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary-hover',
  secondary:
    'bg-surface text-fg border border-border-strong hover:bg-surface-muted ' +
    'carbon:border-0 carbon:bg-secondary carbon:text-secondary-foreground ' +
    'carbon:hover:bg-secondary-hover',
  ghost:
    'bg-transparent text-fg hover:bg-surface-muted ' +
    'carbon:text-primary carbon:hover:bg-surface-hover carbon:hover:text-[var(--color-link-hover)]',
  danger: 'bg-danger text-danger-foreground hover:opacity-90',
  // Sayfa düzeyinde kırmızı ÇERÇEVE, dolu kırmızı DEĞİL (S34-1 A3, web-ui-designer review M15) —
  // `Reddet` (izin/kontrol) + `Kapat` butonlarının üçüncü ayrı tüketicisi aynı className
  // override'ı taşıyordu; artık tek yerden bakım. Dolu kırmız yalnız onay diyaloglarının
  // İÇİNDEKİ butonlarda (`variant="danger"`, dokunulmadı).
  'danger-tertiary':
    'border border-danger bg-transparent text-danger hover:bg-danger hover:text-danger-foreground',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-11 px-5 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    asChild = false,
    loading = false,
    className,
    disabled,
    children,
    ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      ref={ref}
      className={cn(base, variants[variant], sizes[size], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {children}
    </Comp>
  );
});
